// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

// ==========================================
// INTERVIEW ORCHESTRATOR
// Main entry component - Real API Integration
// ==========================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './Interview.css';
import IdentityVerification from './IdentityVerification';
import OTPVerification from './OTPVerification';
import ReadinessCheck from './ReadinessCheck';
import VoiceInterview from './VoiceInterview';
import ContactSupportModal from '../../components/Interview/ContactSupportModal';
import { interviewAPI } from '../../services/interview/api';
import { SessionValidationResponse, InterviewStep } from '../../types/interview';
import { getSessionStatusMessage, canProceedWithInterview } from '../../utils/interviewStatusMessages';

const Interview: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<InterviewStep>('identity');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionValidationResponse | null>(null);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [proficiencyLevel, setProficiencyLevel] = useState<number>(3);

  useEffect(() => {
    validateSession();
  }, [token]);

  const validateSession = async (): Promise<void> => {
    // Check if token is provided
    if (!token) {
      const statusMessage = getSessionStatusMessage('INVALID');
      setError(statusMessage.message);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await interviewAPI.validateSession(token);

      // Check if session is valid and user can proceed
      if (response.sessionValid && canProceedWithInterview(response.status)) {
        setSessionData(response);
        // Handle IN_PROGRESS status - might want to resume from a different step
        if (response.status === 'IN_PROGRESS') {
          // Could add logic here to determine which step to resume from
          setCurrentStep('identity');
        } else {
          setCurrentStep('identity');
        }
      } else {
        // Get appropriate status message
        const statusMessage = getSessionStatusMessage(response.status);
        setError(statusMessage.message);
        setSessionData(response); // Store response for displaying additional info
      }
    } catch (err) {
      setError('Failed to connect to the server. Please check your internet connection and try again.');
      console.error('Session validation error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async (): Promise<void> => {
    if (!sessionData?.workEmail || !token) {
      throw new Error('Missing session data');
    }

    await interviewAPI.resendOTP({
      token: token,
      workEmail: sessionData.workEmail
    });
  };

  const handleStartInterview = async (): Promise<void> => {
    console.log('Starting interview for session:', sessionData?.sessionId);
    if (!token) return;
    const consent = await interviewAPI.recordConsent(token);
    if (!consent.success) {
      setError(consent.message || 'Unable to record consent. Please try again.');
      return;
    }
    setCurrentStep(sessionData?.assessmentType === 'skill_assessment' ? 'proficiency' : 'interview');
  };

  const handleStartSkillAssessment = (): void => {
    setCurrentStep('interview');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="interview-loading-container">
        <div className="interview-loading-card">
          <div className="interview-spinner" />
          <p className="interview-loading-text">Validating your session...</p>
        </div>
      </div>
    );
  }

  // Error state or invalid session
  if (error || (sessionData && !canProceedWithInterview(sessionData.status))) {
    const handleSupportClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
      e.preventDefault();
      setIsSupportModalOpen(true);
    };

    // Get status message configuration
    const status = sessionData?.status || 'INVALID';
    const statusConfig = getSessionStatusMessage(status);

    return (
      <div className="interview-error-container">
        <div className="interview-error-card">
          <div className="interview-error-icon">{statusConfig.icon}</div>
          <h2 className="interview-error-title">{statusConfig.title}</h2>
          <p className="interview-error-message">
            {error || statusConfig.message}
            {statusConfig.actionText && (
              <>
                {' '}
                {statusConfig.actionText}{' '}
                <a href="#" onClick={handleSupportClick} className="interview-error-link">
                  contact support
                </a>
                .
              </>
            )}
            {!statusConfig.actionText && !error && (
              <>
                {' '}
                <a href="#" onClick={handleSupportClick} className="interview-error-link">
                  Contact support
                </a>{' '}
                if you need assistance.
              </>
            )}
          </p>
        </div>
        <ContactSupportModal isOpen={isSupportModalOpen} onClose={() => setIsSupportModalOpen(false)} />
      </div>
    );
  }

  // Ensure sessionData exists before rendering steps
  if (!sessionData) {
    return null;
  }

  // Render appropriate step
  if (currentStep === 'identity' && sessionData.candidateName && sessionData.workEmail && token) {
    const jobTitle = sessionData.interviewTitle || 'Interview';
    const jobMeta = sessionData.interviewSubtitle || '';

    return (
      <IdentityVerification
        name={sessionData.candidateName}
        email={sessionData.workEmail}
        jobTitle={jobTitle}
        jobMeta={jobMeta}
        sessionToken={token}
        assessmentType={sessionData.assessmentType}
        onNext={() => setCurrentStep('otp')}
      />
    );
  }

  if (currentStep === 'otp' && sessionData.workEmail && token) {
    return (
      <OTPVerification
        email={sessionData.workEmail}
        sessionToken={token}
        onNext={() => setCurrentStep('readiness')}
        onResend={handleResendOTP}
      />
    );
  }

  if (currentStep === 'readiness') {
    return <ReadinessCheck onStart={handleStartInterview} />;
  }

  if (currentStep === 'proficiency') {
    return (
      <div className="interview-proficiency-container">
        <div className="interview-proficiency-panel">
          <p className="interview-proficiency-kicker">AI Skill Assessment</p>
          <h1 className="interview-proficiency-title">Select your proficiency level</h1>
          <div className="interview-proficiency-options" role="radiogroup" aria-label="AI proficiency level">
            {[1, 2, 3, 4, 5].map((level) => {
              const tooltipId = `proficiency-tooltip-${level}`;
              return (
                <div key={level} className="interview-proficiency-option-wrapper">
                  <button
                    type="button"
                    className={`interview-proficiency-option${proficiencyLevel === level ? ' is-selected' : ''}`}
                    onClick={() => setProficiencyLevel(level)}
                    role="radio"
                    aria-checked={proficiencyLevel === level}
                    aria-describedby={tooltipId}
                    aria-label={proficiencyLabel(level)}
                  >
                    <span className="interview-proficiency-number">{level}</span>
                    <span className="interview-proficiency-label">{proficiencyLabel(level)}</span>
                  </button>
                  <div id={tooltipId} className="interview-proficiency-tooltip" role="tooltip">
                    <div className="interview-proficiency-tooltip-title">
                      Level {level} — {proficiencyLabel(level)}
                    </div>
                    <div className="interview-proficiency-tooltip-description">{proficiencyDescription(level)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <button className="interview-proficiency-start" type="button" onClick={handleStartSkillAssessment}>
            Start assessment
          </button>
        </div>
      </div>
    );
  }

  if (currentStep === 'interview' && sessionData.candidateName && token) {
    const jobTitle = sessionData.interviewTitle || 'Interview';

    // Debug logging for session identification
    console.log('[Interview] Starting interview with session data:', {
      sessionId: sessionData.sessionId,
      assessmentType: sessionData.assessmentType,
      isSkillAssessment: sessionData.assessmentType === 'skill_assessment',
      sessionIdPrefix: sessionData.sessionId?.substring(0, 6)
    });

    return (
      <VoiceInterview
        sessionToken={token}
        interviewId={sessionData.sessionId}
        candidateName={sessionData.candidateName}
        jobTitle={jobTitle}
        proficiencyLevel={sessionData.assessmentType === 'skill_assessment' ? proficiencyLevel : undefined}
        interviewDuration={sessionData.interviewDuration}
      />
    );
  }

  return null;
};

export default Interview;

function proficiencyLabel(level: number): string {
  const labels: Record<number, string> = {
    1: 'AI Aware',
    2: 'AI Literate',
    3: 'AI Practitioner',
    4: 'AI Fluent',
    5: 'AI Expert'
  };
  return labels[level] || labels[3];
}

function proficiencyDescription(level: number): string {
  const descriptions: Record<number, string> = {
    1: 'Understands what AI is, why it matters, and can describe its basic concepts and societal impact.',
    2: 'Uses AI tools for simple tasks with basic understanding of prompting, risks, and output evaluation.',
    3: 'Integrates AI into regular workflows, builds small automations, and selects appropriate tools for specific use cases.',
    4: 'Designs and builds production-grade AI solutions, understands architecture trade-offs, and quantifies business impact.',
    5: 'Architects, optimizes, and governs enterprise-scale AI systems with deep technical mastery and end-to-end lifecycle ownership.'
  };
  return descriptions[level] || descriptions[3];
}
